import { useCallback } from "react";

import { queryKeys } from "../queryKeys";
import {
  type FPSLStatus,
  type FPSLTier,
  getFPSLStatus,
  getFPSLTiers,
  type Product,
} from "../rest/account";
import {
  getAllSecfiLoans,
  getSecfiLoanAnalytics,
  type SecfiLoan,
  type SecfiLoanAnalytics,
} from "../rest/loans";
import useRequest, { type QueryOptions } from "./useRequest";

export const useGetFPSLTiers = ({
  accountID,
  product,
  options,
}: {
  accountID?: string;
  product?: Product;
  options?: QueryOptions<FPSLTier[]>;
}) => {
  const {
    data: tiers,
    refetch,
    isLoading,
  } = useRequest(
    queryKeys.fpsl.tiers(accountID || ""),
    () => getFPSLTiers(accountID ?? ""),
    {
      refetchInterval: false,
      ...options,
      enabled: !!accountID && product === "live" && options?.enabled !== false,
    },
  );

  return { tiers, refetch, isLoading };
};

export const useGetFPSLStatus = ({
  accountID,
  product,
  options,
}: {
  accountID?: string;
  product?: Product;
  options?: QueryOptions<FPSLStatus>;
}) => {
  const {
    data: status,
    refetch,
    isLoading,
  } = useRequest(
    queryKeys.fpsl.status(accountID || ""),
    () => getFPSLStatus(accountID ?? ""),
    {
      refetchInterval: false,
      ...options,
      enabled: !!accountID && product === "live" && options?.enabled !== false,
    },
  );

  return { status, refetch, isLoading };
};

export const useGetFPSLTierRates = ({
  accountID,
  product,
}: {
  accountID?: string;
  product?: Product;
}) => {
  const { tiers, isLoading } = useGetFPSLTiers({ accountID, product });

  const eliteTier = tiers?.find((tier) => tier.tier_name === "elite");
  const defaultTier = tiers?.find((tier) => tier.tier_name === "default");

  const toRate = (tier: FPSLTier | undefined) => {
    if (!tier || typeof tier.customer_split !== "number") {
      return null;
    }
    const split = tier.customer_split * 100;
    return Number.isFinite(split) ? split.toFixed(0) : null;
  };

  return {
    eliteRate: toRate(eliteTier),
    defaultRate: toRate(defaultTier),
    isLoading,
  };
};

export const useGetFPSLAnalytics = ({
  accountID,
  product,
  options,
}: {
  accountID?: string;
  product?: Product;
  options?: QueryOptions<SecfiLoanAnalytics>;
}) => {
  const {
    data: analytics,
    refetch,
    isLoading,
  } = useRequest(
    queryKeys.fpsl.analytics(accountID || ""),
    () => getSecfiLoanAnalytics({ accountId: accountID ?? "" }),
    {
      refetchInterval: false,
      ...options,
      enabled: !!accountID && product === "live" && options?.enabled !== false,
    },
  );

  return { analytics, refetch, isLoading };
};

export interface UseLoansParams {
  symbol?: string;
  dateRange?: [Date | null, Date | null];
  sortOrder?: "asc" | "desc";
}

export const useGetFPSLLoans = ({
  accountID,
  product,
  params,
  options,
}: {
  accountID?: string;
  product?: Product;
  params?: UseLoansParams;
  options?: QueryOptions<SecfiLoan[]>;
}) => {
  const selectFunction = useCallback(
    (data: SecfiLoan[]) => {
      const symbol = params?.symbol?.trim();
      if (symbol) {
        const symbolLower = symbol.toLowerCase();
        return data.filter((loan) =>
          (loan.symbol ?? "").toLowerCase().includes(symbolLower),
        );
      }

      return data;
    },
    [params?.symbol],
  );

  const {
    data: loans,
    refetch,
    isLoading,
  } = useRequest(
    queryKeys.fpsl.loans(
      accountID || "",
      params?.dateRange,
      params?.sortOrder ?? "desc",
    ),
    async () => {
      if (!accountID) {
        return [];
      }

      // Convert date range to ISO strings for API
      const start = params?.dateRange?.[0]?.toISOString().split("T")[0];
      const end = params?.dateRange?.[1]?.toISOString().split("T")[0];

      // Fetch all loans (always ascending from API)
      return getAllSecfiLoans({
        accountId: accountID,
        start,
        end,
        pageLimit: 1000,
        sortOrder: params?.sortOrder ?? "desc",
      });
    },
    {
      refetchInterval: false,
      ...options,
      enabled: !!accountID && product === "live" && options?.enabled !== false,

      // Client-side filtering
      select: selectFunction,
    },
  );

  return { loans, refetch, isLoading };
};
